
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useState } from "react";
import { NewGoalDialog } from "@/components/NewGoalDialog";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Goal } from "@/types/todo";
import { AuthButton } from "@/components/auth/AuthButton";
import { User } from "@supabase/supabase-js";
import { useNavigate } from "react-router-dom";
import { WelcomeBanner } from "@/components/dashboard/WelcomeBanner";
import { FeedbackTile } from "@/components/dashboard/FeedbackTile";
import { AdminBroadcastBanner } from "@/components/dashboard/AdminBroadcastBanner";
import { CreateIntentPicker, CreateIntent } from "@/components/onboarding/CreateIntentPicker";
import { BUCKET_LIST_CATEGORY } from "@/constants/categories";

interface GoalHeaderProps {
  onGoalCreated: (goal: Goal) => void;
  categories: string[];
  user: User | null;
  showHeaderControls?: boolean;
}

export function GoalHeader({
  onGoalCreated,
  categories,
  user,
  showHeaderControls = true,
}: GoalHeaderProps) {
  const [isNewGoalDialogOpen, setIsNewGoalDialogOpen] = useState(false);
  const [intentPickerOpen, setIntentPickerOpen] = useState(false);
  const [preselectedCategory, setPreselectedCategory] = useState<string | undefined>();
  const navigate = useNavigate();

  const handleIntentPicked = (intent: CreateIntent) => {
    if (intent === "gift") {
      navigate('/wishlist');
      return;
    }
    if (intent === "certificate") {
      navigate("/certificate/new", { state: { preSelectedType: false } });
      return;
    }
    setPreselectedCategory(intent === "dream" ? BUCKET_LIST_CATEGORY : undefined);
    setIsNewGoalDialogOpen(true);
  };

  return (
    <div className="flex flex-col gap-4 sm:gap-5 mb-2">
      <WelcomeBanner user={user} />
      {user && <AdminBroadcastBanner />}
      {/* Desktop: feedback (8) + create action (4) share one editorial row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 items-stretch">
      <div className="lg:col-span-8 [&>*]:h-full">
        <FeedbackTile />
      </div>

      <div className="lg:col-span-4 flex flex-col sm:flex-row gap-4">
        {showHeaderControls && (
          <div className="flex items-center gap-2">
            <AuthButton />
            <ThemeToggle />
          </div>
        )}

        <div className="flex flex-col gap-1.5 flex-1">
          <Button
            onClick={() => setIntentPickerOpen(true)}
            className="
              w-full
              [background-image:var(--gradient-primary)]
              hover:brightness-105
              text-primary-foreground
              transform hover:scale-[1.01] transition-all duration-200
              shadow-lg hover:shadow-xl
              py-6
              text-base sm:text-lg font-semibold
              whitespace-nowrap
              border-none
              min-h-[56px]
              animate-fade-in
              flex-col gap-0.5
            "
          >
            <span className="flex items-center"><Plus className="mr-2 h-5 w-5" />Create</span>
            <span className="text-[11px] sm:text-xs font-medium opacity-90">
              Goal · Gift · Certificate
            </span>
          </Button>
        </div>

        <NewGoalDialog
          open={isNewGoalDialogOpen}
          onOpenChange={setIsNewGoalDialogOpen}
          onGoalCreated={async (goal) => {
            await onGoalCreated(goal);
            if (goal?.id) navigate(`/goals/${goal.id}`);
          }}
          categories={categories}
          initialCategory={preselectedCategory}
        />

        <CreateIntentPicker
          open={intentPickerOpen}
          onOpenChange={setIntentPickerOpen}
          onPick={handleIntentPicked}
        />
      </div>
      </div>
    </div>
  );
}
